import { DocumentFormat } from 'epic/common-form/Documentation';
import type { DocumentType } from 'models/documents/DocumentType';
import type { BlobFileResponse } from 'models/FileResponse';
import type { BackendError, NextWorkflowError } from 'models/NextWorkflowError';
import type { Observable } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';
import { httpClient } from 'utils/http/httpClient';
import { type HttpError, type IHttpClient, isBackendErrorMessage, isClientError } from 'utils/http/httpClient.model';

export type BlobWithFilename = {
  blob: Blob;
  filename?: string;
};

export type DocumentDownloadResult = Blob | BlobWithFilename;

export function isBlobWithFilename(result: DocumentDownloadResult): result is BlobWithFilename {
  return typeof result === 'object' && result !== null && 'blob' in result && result.blob instanceof Blob;
}

export type DocumentBlobOrAcceptedStatus = BlobWithFilename | 'Accepted';

export type Technical = {
  type: 'TECHNICAL';
  error?: AjaxError;
};

export type Rejected = {
  type: 'REJECTION';
  rejections: NextWorkflowError[];
};

export type EndUserError = {
  type: 'END_USER_ERROR';
  errorMessages: string[];
};

export type DocumentError = Rejected | Technical | EndUserError;

function mapBackendError(err: HttpError<BackendError>): DocumentError {
  if (isBackendErrorMessage(err.payload)) {
    return {
      type: 'END_USER_ERROR',
      errorMessages: [`${err.payload.origin}: ${err.payload.message}`],
    } satisfies DocumentError;
  }
  if (isClientError(err)) {
    const errorResponse = err.payload!;

    if (errorResponse.rejections !== undefined) {
      return { type: 'REJECTION', rejections: [...errorResponse.rejections] };
    }
  }
  return { type: 'TECHNICAL' } satisfies DocumentError;
}
export const documentApiConnector = (http$: IHttpClient) => ({
  downloadDocumentByDocumentToken(tokenId: string): Observable<BlobFileResponse> {
    return http$
      .getBlob({
        url: `api/v1/documents/${tokenId}`,
        parseErrorPayload: true,
      })
      .pipe(
        map(response => ({
          data: response.payload,
          filename: response.attachmentName,
        })),
        catchError((err: HttpError<BackendError>) => {
          throw { type: 'TECHNICAL', error: err };
        }),
      );
  },
  downloadTermsheet(productId: string, language?: string): Observable<BlobFileResponse> {
    const params: { [key: string]: any } = {};
    if (language) {
      params.language = language;
    }
    return http$
      .getBlob({
        url: `api/v1/documents/termsheet/${productId}`,
        params,
        tryParseErrorPayload: true,
      })
      .pipe(
        map(response => ({
          data: response.payload,
          filename: response.attachmentName,
        })),
        catchError((err: HttpError<BackendError>) => {
          throw mapBackendError(err);
        }),
      );
  },
  downloadPreconfirmation(productId: string): Observable<BlobFileResponse> {
    return http$
      .getBlob({
        url: `api/v1/documents/preconfirmation/${productId}`,
        tryParseErrorPayload: true,
      })
      .pipe(
        map(response => ({
          data: response.payload,
          filename: response.attachmentName,
        })),
        catchError((err: HttpError<BackendError>) => {
          throw mapBackendError(err);
        }),
      );
  },
  downloadMarketingMaterials(productId: string): Observable<BlobFileResponse> {
    return http$
      .getBlob({
        url: `api/v1/documents/marketingMaterials/${productId}`,
        tryParseErrorPayload: true,
      })
      .pipe(
        map(response => ({
          data: response.payload,
          filename: response.attachmentName,
        })),
        catchError((err: HttpError<BackendError>) => {
          const errorResponse = err.payload;

          if (errorResponse?.rejections !== undefined) {
            throw { type: 'REJECTION', rejections: errorResponse.rejections };
          }
          throw { type: 'TECHNICAL', error: err };
        }),
      );
  },

  generateDocumentV2<T extends DocumentBlobOrAcceptedStatus = DocumentBlobOrAcceptedStatus>(
    quoteQuery: unknown,
    documentType: DocumentType | string,
    locale: string,
    productSubtype?: string,
    quoteId?: string,
    quoteState?: string,
    productType?: string,
    documentFormat?: DocumentFormat,
  ): Observable<T> {
    const language = locale;
    return http$
      .postBlob({
        url: `api/v1/documents/${documentType}`,
        body: { quoteQuery, language, productSubtype, quoteId, quoteState, productType, documentFormat } as any,
        parseErrorPayload: true,
      })
      .pipe(
        map(({ status, payload, attachmentName }) => {
          if (status === 202) {
            return 'Accepted' as T;
          }
          return { blob: payload, filename: attachmentName } as T;
        }),
        catchError((err: HttpError<BackendError>) => {
          if (isClientError(err)) {
            const errorResponse = err.payload!;

            if (errorResponse.rejections !== undefined) {
              throw { type: 'REJECTION', rejections: errorResponse.rejections };
            }
          }
          throw { type: 'TECHNICAL', error: err };
        }),
      );
  },
});

export const documentApi = documentApiConnector(httpClient);
